import { type AxiosResponse } from 'axios'

import axiosInstance from 'api/axiosInstance'
import { type Country } from 'types/api/country'
import {
    type BankAccount,
    type GetPayoutSettingsResponse,
    type PayoutDocumentType
} from 'types/api/payout'

export const putBasicPayoutSettings = async (payload: {
    payout_method?: string | null
    payout_frequency?: string | null
    verification_id?: string
}) => {
    const { data } = await axiosInstance({
        method: 'PUT',
        url: '/api/user/payout/settings',
        data: payload
    })

    return data
}

export interface BankFieldDefinition {
    name: string
    label: string
    required: boolean
    type: 'text' | 'select'
    options: Array<{
        value: string
        label: string
    }>
}

export interface BankFieldRequirements {
    country: string
    method: 'wire' | 'ach'
    transferType: 'WIRE' | 'EFT'
    currency: string
    beneficiaryType: 'PERSONAL' | 'BUSINESS'
    fields: BankFieldDefinition[]
}

export const getBankFieldRequirements = async ({
    country,
    method,
    beneficiaryType
}: {
    country: string
    method: 'wire' | 'ach'
    beneficiaryType: 'PERSONAL' | 'BUSINESS'
}): Promise<BankFieldRequirements> => {
    const { data } = await axiosInstance.get<{
        data: BankFieldRequirements
    }>('/api/user/payout/bank-requirements', {
        params: {
            country,
            method,
            currency: 'USD',
            beneficiaryType
        }
    })

    return data.data
}
export const getSupportedPayoutCountries = async (
    method: 'wire' | 'ach'
): Promise<Country[]> => {
    const { data } = await axiosInstance.get<{
        data: Country[]
    }>('/api/user/payout/supported-countries', {
        params: {
            method
        }
    })

    return data.data
}
export const getBankInformation = async () => {
        console.log('[getBankInformation] fired')
    console.trace('[getBankInformation] stack')
    const { data } = await axiosInstance({
        method: 'GET',
        url: '/api/user/payout/bank-details'
    })
    return data?.data
}
export const putPaxumInformation = async (
    payload: { paxum_email: string }
) => {
    const { data } = await axiosInstance({
        method: 'PUT',
        url: '/api/user/payout/paxum-details',
        data: payload
    })

    return data
}
export const getNextPayoutDate = async (
    payout_frequency: 'weekly' | 'monthly'
) => {
    const { data } = await axiosInstance({
        method: 'GET',
        url: '/api/user/payout/next-payout-date',
        params: {
            payout_frequency
        }
    })

    return data?.data?.message?.data
}

export const deleteBankInformation = async () => {
    const { data } = await axiosInstance({
        method: 'DELETE',
        url: '/api/user/payout/bank-details'
    })
    return data
}

export const putBankInformation = async (payload?: Partial<BankAccount> & { paxum_email?: string }) => {
    const { data } = await axiosInstance({
        method: 'PUT',
        url: '/api/user/payout/bank-details',
        data: payload
    })
    return data
}

export const autocompleteBankName = async ({
    query,
    country,
    limit = 10
}: {
    query: string
    country?: string
    limit?: number
}) => {
    const { data } = await axiosInstance({
        method: 'GET',
        url: '/api/user/payout/bank-directory/autocomplete',
        params: {
            q: query,
            country,
            limit
        }
    })

    return data?.message?.data ?? data?.data ?? []
}

export const postDocument = async ({
    file,
    type,
    setProgress
}: {
    file: File
    type: PayoutDocumentType
    setProgress?: (loaded: number, total: number, cancelCb: () => void) => void
}) => {
    try {
        const data = new FormData()
        const controller = new AbortController()

        data.append('file', file)
        data.append('type', type)

        return await axiosInstance({
            method: 'POST',
            url: '/api/user/document/upload-pdf',
            data,
            headers: { 'Content-Type': 'multipart/form-data' },
            signal: controller.signal,
            onUploadProgress: p => {
                setProgress &&
                    setProgress(p.loaded, p.total as number, () => {
                        controller.abort()
                    })
            }
        })
    } catch (error) {
        console.error('Error:', error)
    }
}

export const getPayoutSettings = async () => {
    const { data } = await axiosInstance<
        AxiosResponse<GetPayoutSettingsResponse>
    >({
        method: 'GET',
        url: '/api/payout-settings'
    })
    return data?.data
}
